import { Recipe } from "../../models/recipe";
import { generateId } from "../../utils/generateId";
import { normalizeField } from "../../utils/textNormalize";
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { deleteRecipeAction } from "../actions";
import { updateCookingProgress } from "./cookingProgressSlice";
import { makeSyncUpsert, makeSyncRemove } from "../syncUpsert";

export const recipesAdapter = createEntityAdapter<Recipe>();
type RecipesState = EntityState<Recipe, string>;
const initialState: RecipesState = recipesAdapter.getInitialState();

// Clean paste/typing artifacts (nbsp, zero-width chars, soft hyphens, CRLF) from the
// recipe's free-text fields on save, so manually-entered or pasted values don't carry
// invisible characters that break display wrapping and search. The Quick Paste path
// already runs through parseRecipeText; this covers per-field manual entry on both
// platforms (sync-pulled rows go through a separate upsert and aren't touched).
function normalizeRecipeFields(recipe: Recipe): Recipe {
  const out = { ...recipe };
  if (out.title != null) out.title = normalizeField(out.title);
  if (out.description != null) out.description = normalizeField(out.description);
  if (out.notes != null) out.notes = normalizeField(out.notes);
  if (out.source != null) out.source = normalizeField(out.source);
  if (out.sourceUrl != null) out.sourceUrl = normalizeField(out.sourceUrl);
  if (out.tags) out.tags = out.tags.map((t) => normalizeField(t)).filter(Boolean);
  return out;
}

export const recipesSlice = createSlice({
  name: "recipe",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(recipesAdapter),
    removeManyFromSync: makeSyncRemove(recipesAdapter),
    addRecipe(state: RecipesState, action: PayloadAction<Recipe>): void {
      const recipe = normalizeRecipeFields(action.payload);

      // Keep the caller-provided id — new.tsx generates one up front and reuses
      // it as the recipeId for the ingredients/steps, so they must match. Only
      // generate when none was supplied (never overwrite, or children orphan and
      // fail their recipe_id FK on sync).
      if (!recipe.id) recipe.id = generateId();

      // Stamp createdAt locally so "Added" shows immediately (before the first
      // sync) and stays a stable client value the server preserves. Only when
      // absent — a duplicate/import may deliberately reset or carry its own.
      if (!recipe.createdAt) recipe.createdAt = new Date().toISOString();

      recipesAdapter.addOne(state, recipe);
    },
    // Note: edit reducers do NOT stamp updatedAt — the server's set_updated_at
    // trigger owns it (authoritative for the pull cursor + LWW). Dirty tracking is
    // by reference, so an updateOne is enough to queue a push. Don't re-add bumps.
    updateRecipe(state: RecipesState, action: PayloadAction<Recipe>) {
      const recipe = normalizeRecipeFields(action.payload);

      recipesAdapter.updateOne(state, {
        id: recipe.id,
        changes: { ...recipe },
      });
    },
    toggleFavourite(state, action: PayloadAction<string>) {
      const recipe = state.entities[action.payload];
      if (recipe) {
        recipesAdapter.updateOne(state, {
          id: action.payload,
          changes: { favourite: !recipe.favourite },
        });
      }
    },
    // Set the cooked flag explicitly (e.g. "Mark as not cooked").
    setRecipeCooked(state, action: PayloadAction<{ id: string; cooked: boolean }>) {
      recipesAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { cooked: action.payload.cooked },
      });
    },
    // Set (or clear, with null) the personal 1-5 star rating.
    setRating(state, action: PayloadAction<{ id: string; rating: number | null }>) {
      recipesAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { rating: action.payload.rating },
      });
    },
    // Called by the SyncManager once a local image is uploaded to Storage:
    // store the remote URL and clear the pending flag. This is a normal local
    // change, so it's dirty-tracked and the new URL pushes on the same sync.
    setRecipeImageUploaded(
      state: RecipesState,
      action: PayloadAction<{ id: string; url: string }>,
    ) {
      recipesAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          image: action.payload.url,
          imagePendingUpload: false,
          localImageUri: undefined, // local pick is deleted post-upload; no longer used
        },
      });
    },
    // Records the local cache path for a downloaded remote image. Local-only
    // (omitted from sync); tagged fromSync so it isn't dirty-tracked/re-pushed.
    setRecipeCachedImage: {
      reducer(
        state: RecipesState,
        action: PayloadAction<{ id: string; cachedImageUri: string }>,
      ) {
        recipesAdapter.updateOne(state, {
          id: action.payload.id,
          changes: { cachedImageUri: action.payload.cachedImageUri },
        });
      },
      prepare(payload: { id: string; cachedImageUri: string }) {
        return { payload, meta: { fromSync: true } };
      },
    },
    // Give up on an unrecoverable local pick (its file is gone, e.g. deleted by
    // iOS): drop the dead reference and the pending flag so the row settles and
    // sync stops retrying forever. Local-only fields, so tag fromSync (no push).
    clearRecipePendingImage: {
      reducer(state: RecipesState, action: PayloadAction<{ id: string }>) {
        recipesAdapter.updateOne(state, {
          id: action.payload.id,
          changes: { imagePendingUpload: false, localImageUri: undefined },
        });
      },
      prepare(payload: { id: string }) {
        return { payload, meta: { fromSync: true } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteRecipeAction, (state, action) => {
      recipesAdapter.updateOne(state, {
        id: action.payload,
        changes: { deletedAt: new Date().toISOString() },
      });
    });
    builder.addCase(updateCookingProgress, (state, action) => {
      // Only include cooking progress that contains a recipeId and is completed
      if (action.payload.status !== "completed" || !action.payload.recipeId) return;

      recipesAdapter.updateOne(state, {
        id: action.payload.recipeId,
        changes: {
          cooked: true,
        },
      });
    });
  },
});

const recipeSelectors = recipesAdapter.getSelectors((state: RootState) => state.recipes);

// selectAll* hides soft-deleted rows (tombstones) by default. The sync layer
// reads state.recipes.entities directly, so it doesn't rely on these selectors.
export const selectRecipeById = recipeSelectors.selectById;

export const selectAllRecipes = createSelector(recipeSelectors.selectAll, (recipes) =>
  recipes.filter((r) => !r.deletedAt),
);

export const selectRecipeIds = createSelector(selectAllRecipes, (recipes) =>
  recipes.map((r) => r.id),
);

export const selectRecipesTotal = createSelector(
  selectAllRecipes,
  (recipes) => recipes.length,
);

// Favourite recipes
export const selectFavouriteRecipes = createSelector(selectAllRecipes, (recipes) =>
  recipes.filter((r) => r.favourite),
);

export const selectMostRecentRecipes = (limit: number) =>
  createSelector(selectAllRecipes, (recipes) =>
    [...recipes]
      .sort(
        (a, b) =>
          new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
      )
      .slice(0, limit),
  );

export const selectQuickRecipes = (threshold: number, limit: number = Infinity) =>
  createSelector(selectAllRecipes, (recipes) => {
    // Filter out recipes that do not have a cooking duration, or it's under the threshold for
    const filteredRecipes = recipes.filter(
      (recipe) => recipe.cookingDuration && recipe.cookingDuration <= threshold,
    );

    const sortedRecipes = [...filteredRecipes].sort(
      (a, b) => (a.cookingDuration ?? 0) - (b.cookingDuration ?? 0),
    );

    return sortedRecipes.slice(0, limit);
  });

export const {
  addRecipe,
  updateRecipe,
  toggleFavourite,
  setRecipeCooked,
  setRating,
  setRecipeImageUploaded,
  setRecipeCachedImage,
  clearRecipePendingImage,
  upsertManyFromSync: upsertRecipesFromSync,
  removeManyFromSync: removeRecipesFromSync,
} = recipesSlice.actions;

export default recipesSlice.reducer;
